import { Components } from "appworks/components/components";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { builderManager } from "appworks/server/builders/builder-manager";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { Services } from "appworks/services/services";
import { CommonUIAlertComponent } from "gamevy/components/common-ui-alert-component";
import { gamevy } from "gamevy/gamevy";
import { gamevyModel } from "gamevy/gamevy-model";
import { GamevyPartnerConnectSpinResponseSchema } from "gamevy/integration/schema/partner-connect";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { FreespinRequestPayload } from "slotworks/model/gameplay/requests/freespin-request-payload";
import { SpinRequestPayload } from "slotworks/model/gameplay/requests/spin-request-payload";
import { slotDefinition } from "slotworks/model/slot-definition";
import { convertGamevyGridToSlotworksGrid } from "../../gamevy-utils";
import { ForceCloseRequestPayload } from "../requests/force-request-payload";

export class GamevyPartnerConnectSpinRecordBuilder implements RecordBuilder {

    public id: string = "GamevyPartnerConnectSpinRecordBuilder";

    public filter(request: any, response: any): boolean {
        return request instanceof SpinRequestPayload || request instanceof FreespinRequestPayload || request instanceof ForceCloseRequestPayload;
    }

    public build(gameplay: Gameplay, request: any, response: any): SpinRecord[] {
        if (!response || !response.data || response.data.error) {
            return [];
        }
        let gamevyResponse: GamevyPartnerConnectSpinResponseSchema;
        gamevyResponse = response.data as GamevyPartnerConnectSpinResponseSchema;

        if (gamevyResponse.balance && gamevyResponse.balance.message) {
            Components.get(CommonUIAlertComponent).splitBalanceMessage(gamevyResponse.balance.message).execute();
        } else if (gamevyResponse.message) {
            Components.get(CommonUIAlertComponent).splitBalanceMessage(gamevyResponse.message).execute();
        }

        if (gamevyResponse.gameInstanceId) {
            gamevy.setGameInstanceID(gamevyResponse.gameInstanceId);
        }

        let record: SpinRecord | FreespinRecord;

        // Ongoing responses for some reason nest some of the response inside a state object
        if (gamevyResponse.state) {
            gamevyResponse = gamevyResponse.state;
        }

        // TODO: If Gamevy change schema we should check for freespins that way
        const inFreespins = gamevyResponse.game.type === "freeSpins";
        if (inFreespins) {
            record = new FreespinRecord();
        } else {
            record = new SpinRecord();
            record.wager = Services.bet.getTotalStake();
        }

        record.grid = convertGamevyGridToSlotworksGrid(gamevyResponse.game.reels);
        record.reelsetId = gamevyResponse.game.type;
        record.reelset = slotDefinition.reelsets.get("base");

        if (record instanceof FreespinRecord && slotDefinition.reelsets.has("freespins")) {
            record.reelset = slotDefinition.reelsets.get("freespins");
        }

        if (gamevyResponse.game.outcome) {
            if (gamevyResponse.game.outcome?.type === "win") {
                record.cashWon = gamevyResponse.game.outcome?.amount?.CASH;
            }
        }

        record.gamble = gamevyResponse.isGambleAllowed;
        record.gambleID = gamevyResponse.gameInstanceId;
        record.gambleStake = record.cashWon;

        record.results = [];

        let results;
        const gamevyResults = [];
        // Since there is no obvious structure for results in the response, make a list of everything to pass through to result builders:
        // lines, scatters, payouts, every root object in the response, and every object within "game" object
        if (gamevyResponse.game.lines) {
            gamevyResults.push(...gamevyResponse.game.lines);
        }
        if (gamevyResponse.game.scatter) {
            gamevyResults.push(...gamevyResponse.game.scatter);
        }
        if (gamevyResponse.game.payouts) {
            gamevyResults.push(...gamevyResponse.game.payouts);
        }
        for (const key in gamevyResponse) {
            if (gamevyResponse.hasOwnProperty(key) && key !== "game") {
                const object = gamevyResponse[key];
                gamevyResults.push({ [key]: object });
            }
        }
        for (const key in gamevyResponse.game) {
            if (gamevyResponse.game.hasOwnProperty(key)) {
                const object = gamevyResponse.game[key];
                gamevyResults.push({ [key]: object });
            }
        }

        // Actual result building
        gamevyResults.forEach((obj: any) => {
            results = builderManager.buildResult(record, obj, gamevyResponse);
            record.results.push(...results);
        });

        if (gamevyResponse.balance) {
            if (gamevyResponse.balance.CASH !== undefined) {
                gameplay.balance = gamevyResponse.balance.CASH;
            }

            if (gamevyResponse.balance.FREE) {
                for (const key of Object.keys(gamevyResponse.balance.FREE)) {
                    const betKey: number = parseInt(key);
                    if (betKey) {
                        gamevyModel.setFreeGames(betKey, gamevyResponse.balance.FREE[key]);
                    } else if (key === "totalWin") {
                        gamevyModel.setFreegamesWinnings(gamevyResponse.balance.FREE[key]);
                    }
                }
            } else {
                gamevyModel.clearFreeGames();
            }
        } else {
            gameplay.balance = gameplay.startBalance;
        }

        if (gamevyResponse.outcome?.type === "win" && !gamevyResponse.isGambleAllowed) {
            gameplay.balance += record.cashWon;

            if (record instanceof FreespinRecord && gamevyResponse?.remainingSpins === 0) {
                gameplay.balance += gameplay.getCurrentTotalWin();
            }
        }

        const freespinsAwarded = !!gamevyResponse.game.payouts.find((payout) => payout.freeSpins && payout.freeSpins > 0);

        record.hasChildren = record.gamble || freespinsAwarded;

        if (!freespinsAwarded && !gamevyResponse.remainingSpins) {
            record.lastChild = true;
        }

        if (record instanceof FreespinRecord && freespinsAwarded) {
            record.hasChildren = false;
        }

        return [record];
    }
}

export const gamevyPartnerConnectSpinRecordBuilder = new GamevyPartnerConnectSpinRecordBuilder();
